import {
  ColDef,
  ColGroupDef,
  Column,
  ColumnMenuTab,
  GridApi
} from "ag-grid-enterprise";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";

import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { DateFilter, DateUtils } from "utils/dateutils";

type MaxMileStoneInfo = {
  numberOfMaxMileStones: number;
  numberOfMaxOffsets: number;
};
type TColIds = {
  colId: string;
  children: {
    index: number;
    colIds: string[];
  }[];
};

const getCurrentMilestone = (
  data: { milestones: Array<any> },
  sequenceNo: number
) => {
  return data.milestones.find((m) => m.sequence_no === sequenceNo);
};

export const getCurrentDuration = (
  data: { milestones: Array<any>; offsets: Array<any> },
  sequenceNo: number
) => {
  const currentMilestoneID = getCurrentMilestone(data, sequenceNo)?.id;
  return data.offsets.find(
    (o) => o.from_milestone_instance_id === currentMilestoneID
  );
};

export function EntityMilestoneAndDurationColumns(entityList: any[]) {
  const maxNumberOfMilestones = entityList?.reduce((max: number, item: any) => {
    const milestones = item?.milestones || [];
    if (!Array.isArray(milestones) || milestones.length === 0) {
      return max;
    }
    return Math.max(max, milestones.length);
  }, 0);
  const groupColInitData = {
    width: 0,
    maxWidth: 0,
    headerClass: "!p-0 !w-0",
    filter: false,
    editable: false,
    suppressMovable: true,
    getQuickFilterText: () => "",
    hide: true,
    lockPosition: "right"
  } as ColDef;

  const colInitData = {
    hide: true,
    // Must be visible in the Columns Tool Panel when referenced via setColumnLayout
    // (e.g. design/bid package tree layout). Keeping this suppressed hides leaf nodes.
    suppressColumnsToolPanel: false,
    suppressMovable: true,
    menuTabs: ["filterMenuTab"] as ColumnMenuTab[],
    editable: false,
    width: 180,
    keyCreator: ({ value }: any) => value,
    getQuickFilterText: () => "",
    lockPosition: "right"
  } as ColDef;
  const totalPossibleMileStones = maxNumberOfMilestones;

  const milestoneHead = {
    ...groupColInitData,
    headerName: "Milestones",
    colId: "_milestones"
  };
  const durationHead = {
    ...groupColInitData,
    headerName: "Durations",
    colId: "_durations"
  };
  const allColumns =
    maxNumberOfMilestones > 0
      ? ([milestoneHead, durationHead] as ColDef[])
      : ([] as ColDef[]);

  const milestoneChildColIds = {
    colId: milestoneHead.colId,
    children: []
  } as TColIds;
  const durationChildColIds = {
    colId: durationHead.colId,
    children: []
  } as TColIds;

  const columnIds = [milestoneChildColIds, durationChildColIds];

  for (let index = 1; index <= totalPossibleMileStones; index = 1 + index) {
    const mColId1 = `m1_${index}`;
    const mColId2 = `m2_${index}`;
    const mColId3 = `m3_${index}`;
    const mColId4 = `m4_${index}`;
    milestoneChildColIds.children.push({
      index,
      colIds: [mColId1, mColId2, mColId3, mColId4]
    });

    const dColId1 = `du1_${index}`;
    const dColId2 = `du2_${index}`;
    durationChildColIds.children.push({
      index,
      colIds: [dColId1, dColId2]
    });

    allColumns.push({
      ...colInitData,
      colId: mColId1,
      headerName: `Milestone Name ${index}`,
      valueGetter: ({ data }: any) => {
        const currentMilestone = getCurrentMilestone(data, index);
        return currentMilestone ? currentMilestone.name : undefined;
      }
    });
    allColumns.push({
      ...colInitData,
      colId: mColId2,
      headerName: `Planned Date ${index}`,
      filter: CustomDateSetFilter,
      valueGetter: ({ data }: any) => {
        const currentMilestone = getCurrentMilestone(data, index);
        return currentMilestone?.planned_date
          ? DateUtils.format(currentMilestone.planned_date)
          : undefined;
      },
      comparator: DateFilter.comparator
    });
    allColumns.push({
      ...colInitData,
      colId: mColId3,
      headerName: `Projected Date ${index}`,
      filter: CustomDateSetFilter,
      valueGetter: ({ data }: any) => {
        const currentMilestone = getCurrentMilestone(data, index);
        return currentMilestone?.projected_date
          ? DateUtils.format(currentMilestone.projected_date)
          : undefined;
      },
      comparator: DateFilter.comparator
    });
    allColumns.push({
      ...colInitData,
      colId: mColId4,
      headerName: `Actual Date ${index}`,
      filter: CustomDateSetFilter,
      valueGetter: ({ data }: any) => {
        const currentMilestone = getCurrentMilestone(data, index);
        return currentMilestone?.actual_date
          ? DateUtils.format(currentMilestone.actual_date)
          : undefined;
      },
      comparator: DateFilter.comparator
    });
    allColumns.push({
      ...colInitData,
      colId: dColId1,
      headerName: `Duration Name ${index}`,
      valueGetter: ({ data }: any) => {
        const currentDuration = getCurrentDuration(data, index);
        return currentDuration ? currentDuration.name : undefined;
      }
    });
    allColumns.push({
      ...colInitData,
      colId: dColId2,
      headerName: `Duration ${index}`,
      filter: "agNumberColumnFilter",
      filterParams: numberFilterParams,
      valueGetter: ({ data }: any) => {
        const currentDuration = getCurrentDuration(data, index);
        return currentDuration ? currentDuration.duration : undefined;
      },
      editable: true,
      cellClass: ({ data, context }: any) => {
        if (!context?.editPermission) return "";
        const currentDuration = getCurrentDuration(data, index);
        return currentDuration?.is_editable ? "cell-editable" : "";
      },
      cellEditor: "agNumberCellEditor",
      cellEditorParams: numberCellEditorParams
    });
  }

  type Props = {
    column: Column | null;
    api: GridApi;
  };

  const onColumnVisible = (e: Props, milestoneInfo: MaxMileStoneInfo) => {
    const { column, api } = e;
    const colId = column?.getColId();
    const extraColumn = columnIds.find((c) => c.colId === colId);
    if (!extraColumn) return;

    const isVisible = column?.isVisible() || false;
    const colIdsColumns = [] as string[];
    const hideColumnIds = [] as string[];
    let count = milestoneInfo.numberOfMaxMileStones;
    if (extraColumn.colId === "_durations") {
      count = milestoneInfo.numberOfMaxOffsets;
    }

    extraColumn.children.forEach((c) => {
      if (count >= c.index) {
        c.colIds.forEach((id) => colIdsColumns.push(id));
      } else {
        c.colIds.forEach((id) => hideColumnIds.push(id));
      }
    });

    api.setColumnsVisible(colIdsColumns, isVisible);
    api.setColumnsVisible(hideColumnIds, false);
  };

  const updateAllMileStoneInitialVisibility = (
    api: GridApi,
    milestoneInfo: MaxMileStoneInfo
  ) => {
    if (!api) return;

    const colIds = columnIds.map((x) => x.colId);
    colIds.forEach((colId) => {
      const column = api.getColumn(colId);
      if (column) {
        onColumnVisible(
          {
            column,
            api
          },
          milestoneInfo
        );
      }
    });
  };

  return { allColumns, onColumnVisible, updateAllMileStoneInitialVisibility };
}

/**
 * Sidebar column layout for bid/design package grids (tree in tool panel).
 * Uses m1_, m2_, m3_ (milestones) and du1_, du2_ (durations) column IDs.
 */
export function getSidebarColumnLayoutForBidDesignPackages(
  numberOfMaxMilestones: number,
  numberOfMaxOffsets: number
) {
  const milestonesCount = numberOfMaxMilestones || 0;
  const offsetsCount = numberOfMaxOffsets || 0;
  const nonDraggableToolPanelClass = "md-tree-non-draggable";

  const milestonesGroupCol: ColGroupDef = {
    headerName: "Milestones",
    groupId: "milestones_group",
    openByDefault: false,
    toolPanelClass: nonDraggableToolPanelClass,
    children: []
  };
  const durationsGroupCol: ColGroupDef = {
    headerName: "Durations",
    groupId: "durations_group",
    openByDefault: false,
    toolPanelClass: nonDraggableToolPanelClass,
    children: []
  };
  const sidebarColumnLayout: (ColDef | ColGroupDef)[] = [
    milestonesGroupCol,
    durationsGroupCol
  ];

  for (let i = 1; i <= milestonesCount; i += 1) {
    (milestonesGroupCol.children as ColGroupDef[]).push({
      headerName: `Milestone ${i}`,
      groupId: `milestone_group_${i}`,
      openByDefault: false,
      toolPanelClass: nonDraggableToolPanelClass,
      children: [
        {
          colId: `m1_${i}`,
          headerName: `Milestone Name ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `m2_${i}`,
          headerName: `Planned Date ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `m3_${i}`,
          headerName: `Projected Date ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `m4_${i}`,
          headerName: `Actual Date ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        }
      ]
    });
  }

  for (let i = 1; i <= offsetsCount; i += 1) {
    (durationsGroupCol.children as ColGroupDef[]).push({
      headerName: `Duration ${i}`,
      groupId: `duration_group_${i}`,
      openByDefault: false,
      toolPanelClass: nonDraggableToolPanelClass,
      children: [
        {
          colId: `du1_${i}`,
          headerName: `Duration Name ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `du2_${i}`,
          headerName: `Duration ${i}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        }
      ]
    });
  }

  return { sidebarColumnLayout };
}

export const getMaxNumberOfMilestone = (props: {
  api: GridApi<any> | undefined;
}) => {
  const { api } = props;

  if (!api) return null;
  const count = api?.getDisplayedRowCount();

  const milestoneHasValue = (m: any) => {
    return (
      (m?.name != null && String(m.name).trim() !== "") ||
      m?.planned_date != null ||
      m?.projected_date != null ||
      m?.actual_date != null
    );
  };

  const offsetHasValue = (o: any) => {
    return (
      (o?.name != null && String(o.name).trim() !== "") || o?.duration != null
    );
  };

  const getEffectiveMilestonesCount = (milestones: any[] | undefined) => {
    if (!Array.isArray(milestones) || milestones.length === 0) return 0;
    let maxIdx = 0;
    milestones.forEach((m: any, idx: number) => {
      const seq = typeof m?.sequence_no === "number" ? m.sequence_no : idx + 1;
      if (milestoneHasValue(m)) {
        maxIdx = Math.max(maxIdx, seq);
      }
    });
    return maxIdx;
  };

  const getEffectiveOffsetsCount = (offsets: any[] | undefined) => {
    if (!Array.isArray(offsets) || offsets.length === 0) return 0;
    let maxIdx = 0;
    offsets.forEach((o: any, idx: number) => {
      const seq =
        typeof o?.sequence_no === "number"
          ? o.sequence_no
          : typeof o?.from_sequence_no === "number"
            ? o.from_sequence_no
            : idx + 1;
      if (offsetHasValue(o)) {
        maxIdx = Math.max(maxIdx, seq);
      }
    });
    return maxIdx;
  };

  let maxMilestoneCount = 0;
  let maxOffsetCount = 0;
  for (let index = 0; index < count; index = 1 + index) {
    const element = api?.getDisplayedRowAtIndex(index);
    if (element && element.data) {
      maxMilestoneCount = Math.max(
        maxMilestoneCount,
        getEffectiveMilestonesCount(element.data.milestones)
      );
      maxOffsetCount = Math.max(
        maxOffsetCount,
        getEffectiveOffsetsCount(element.data.offsets)
      );
    }
  }

  return {
    numberOfMaxOffsets: maxOffsetCount,
    numberOfMaxMileStones: maxMilestoneCount
  };
};
